import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { GoogleLogin } from '@react-oauth/google';
import { Alert } from '../components/Alert';
import axios from 'axios';
import { getEnv } from '../config';

const API_URL = getEnv('REACT_APP_API_URL') || '';

export function Signup() {
    const [searchParams] = useSearchParams();
    const inviteToken = searchParams.get('token');
    const navigate = useNavigate();
    const { acceptInvite, loading, error, setError, isAuthenticated } = useAuth();

    const [inviteInfo, setInviteInfo] = useState(null);
    const [fetchError, setFetchError] = useState(null);
    const [fetching, setFetching] = useState(true);
    const appleScriptLoaded = useRef(false);

    const googleClientId = getEnv('REACT_APP_GOOGLE_CLIENT_ID');
    const appleClientId = getEnv('REACT_APP_APPLE_CLIENT_ID');

    useEffect(() => {
        if (isAuthenticated) navigate('/dashboard');
    }, [isAuthenticated, navigate]);

    useEffect(() => {
        if (!inviteToken) { setFetchError('No invite token provided.'); setFetching(false); return; }
        axios.get(`${API_URL}/auth/invite/${inviteToken}`)
            .then((res) => setInviteInfo(res.data.data))
            .catch(() => setFetchError('This invite link is invalid or has already been used.'))
            .finally(() => setFetching(false));
    }, [inviteToken]);

    useEffect(() => {
        if (!appleClientId || appleScriptLoaded.current) return;
        const script = document.createElement('script');
        script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
        script.async = true;
        script.onload = () => { appleScriptLoaded.current = true; };
        document.head.appendChild(script);
        return () => { document.head.removeChild(script); };
    }, [appleClientId]);

    const handleGoogleSuccess = async (response) => {
        const success = await acceptInvite(response.credential, 'google', inviteToken);
        if (success) navigate('/dashboard');
    };

    const handleGoogleError = () => setError('Google sign-in failed. Please try again.');

    const logToBackend = (msg) => {
        try { axios.post(`${API_URL}/logs`, { level: 'error', message: msg }); } catch {}
    };

    const handleAppleSignIn = async () => {
        try {
            if (!window.AppleID) {
                const msg = `Apple SDK not loaded. appleClientId=${appleClientId}, scripts=${document.querySelectorAll('script[src*="appleid"]').length}`;
                logToBackend(msg); setError(msg); return;
            }
            const redirectURI = window.location.origin + '/signup';
            logToBackend(`Apple signIn (invite) starting: clientId=${appleClientId}, redirectURI=${redirectURI}`);
            window.AppleID.auth.init({ clientId: appleClientId, scope: 'email name', redirectURI, usePopup: true });
            const response = await window.AppleID.auth.signIn();
            logToBackend(`Apple signIn (invite) response received: hasCode=${!!response?.authorization?.code}, hasIdToken=${!!response?.authorization?.id_token}`);
            const code = response.authorization.code;
            const idToken = response.authorization.id_token;
            const success = await acceptInvite(idToken, 'apple', inviteToken, { code, redirectURI });
            if (success) navigate('/dashboard');
        } catch (err) {
            const errDetail = JSON.stringify({ error: err.error, message: err.message, type: err.type, stack: err.stack?.substring(0, 500) });
            logToBackend(`Apple signIn (invite) FAILED: ${errDetail}`);
            console.error('Apple Sign In error:', err);
            if (err.error !== 'popup_closed_by_user') {
                setError(`Apple sign-in failed: ${err.error || err.message || 'Unknown error'}. Check console for details.`);
            }
        }
    };

    const renderCard = (content) => (
        <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
            <div className="w-full max-w-sm bg-white dark:bg-slate-800 rounded-xl shadow-2xl p-8 animate-fade-in">
                {content}
            </div>
        </div>
    );

    const header = (subtitle) => (
        <div className="text-center mb-8">
            <img src="/rt-logo.png" alt="RT Care Groups" className="w-16 h-16 mx-auto mb-4 rounded-lg" />
            <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">RT Care Groups</h1>
            {subtitle && <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{subtitle}</p>}
        </div>
    );

    if (fetching) return renderCard(<>{header('Loading invite...')}</>);
    if (fetchError) return renderCard(<>{header()}<Alert type="error">{fetchError}</Alert></>);

    return renderCard(
        <>
            {header(`Welcome, ${inviteInfo?.name}!`)}

            <div className="text-center text-sm text-slate-600 dark:text-slate-400 mb-6">
                {inviteInfo?.email ? (
                    <p>Sign in with <strong>{inviteInfo.email}</strong> to activate your account.</p>
                ) : (
                    <p>Sign in with Google or Apple to link your account and get started.</p>
                )}
            </div>

            {error && <Alert type="error" onClose={() => setError(null)}>{error}</Alert>}

            <div className="space-y-3">
                {googleClientId && (
                    <div className="flex justify-center">
                        <GoogleLogin
                            onSuccess={handleGoogleSuccess}
                            onError={handleGoogleError}
                            theme="outline"
                            size="large"
                            width="300"
                            text="signin_with"
                        />
                    </div>
                )}
                {appleClientId && (
                    <button
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-black text-white font-medium text-sm hover:bg-gray-900 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500/30"
                        onClick={handleAppleSignIn}
                        disabled={loading}
                    >
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                            <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                        </svg>
                        Sign in with Apple
                    </button>
                )}
            </div>

            {loading && <p className="text-center text-sm text-accent-500 mt-4">Activating your account...</p>}
        </>
    );
}
